<template>
  <div>
    <!-- Mobile month navigation (visible on mobile only via CSS) -->
    <div class="cal-mobile-nav">
      <button
        class="cal-nav-btn"
        :disabled="isPastPeriod"
        @click="navigatePrevious"
      >
        ‹
      </button>
      <span class="cal-mobile-nav-period">{{ currentPeriodLabel }}</span>
      <button class="cal-nav-btn" @click="navigateNext">›</button>
    </div>

    <!-- Desktop controls bar -->
    <div class="cal-controls">
      <!-- View toggle -->
      <div class="view-switcher">
        <button
          :class="{ active: calendarView === 'month' }"
          @click="calendarView = 'month'"
        >
          Mois
        </button>
        <button
          :class="{ active: calendarView === 'week' }"
          @click="calendarView = 'week'"
        >
          Semaine
        </button>
      </div>

      <!-- Navigation -->
      <div style="display: flex; align-items: center; gap: 8px">
        <button
          class="cal-nav-btn"
          :disabled="isPastPeriod"
          @click="navigatePrevious"
        >
          ‹
        </button>
        <span class="cal-period">{{ currentPeriodLabel }}</span>
        <button class="cal-nav-btn" @click="navigateNext">›</button>
        <button class="btn" @click="goToToday">Aujourd'hui</button>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="pending"
      style="
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 500px;
      "
    >
      <div class="spinner" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="calendarError"
      style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 500px;
        gap: 16px;
        font-family: var(--mono);
        font-size: 12px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--danger);
      "
    >
      <span>Impossible de charger le calendrier</span>
      <button class="btn" @click="() => refresh()">Réessayer</button>
    </div>

    <!-- Calendar -->
    <EventsCalendar
      v-else
      :events-by-date="eventsByDate"
      :calendar-view="calendarView"
      :current-date="currentDate"
      :selected-date="selectedDay"
      @select-day="selectedDay = $event"
    />

    <!-- Mobile: selected day panel + events list -->
    <CalendarMobileSelectedDay
      :selected-day="selectedDayObj"
      :event-count="selectedDayEvents.length"
    />
    <div class="cal-day-events-list">
      <div v-if="selectedDayEvents.length === 0" class="cal-day-empty">
        Aucun événement ce jour
      </div>
      <EventThumb
        v-for="event in selectedDayEvents"
        :key="event._id"
        :event="event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { API_ENDPOINTS } from '~/config/api';
import type { EventItem } from '@/types/Event';

definePageMeta({ layout: 'search' });

useSeoMeta({
  title: 'Calendrier des Compétitions CrossFit | Crossfit Event Scanner',
  description:
    'Visualisez toutes les compétitions de CrossFit en France dans un calendrier mensuel ou hebdomadaire. Ne manquez aucun événement !',
});

interface EventsByDate {
  date: string;
  events: EventItem[];
  count: number;
}

type CalendarView = 'month' | 'week';

const { fetchWithAuth } = useApiConfig();
const router = useRouter();
const route = useRoute();
const { add: notify } = useNotification();

const calendarView = ref<CalendarView>('month');
const currentDate = ref(dayjs());
const selectedDay = ref<string | null>(dayjs().format('YYYY-MM-DD'));

function initFromQueryParams() {
  const view = route.query.view as CalendarView | undefined;
  const date = route.query.date as string | undefined;
  if (view === 'month' || view === 'week') calendarView.value = view;
  if (date) {
    const parsed = dayjs(date);
    if (parsed.isValid()) currentDate.value = parsed;
  }
}

function updateQueryParams() {
  router.push({
    path: '',
    query: {
      view: calendarView.value,
      date: currentDate.value.format('YYYY-MM-DD'),
    },
  });
}

initFromQueryParams();

const isPastPeriod = computed(() => {
  const today = dayjs();
  if (calendarView.value === 'month') {
    return (
      currentDate.value.isBefore(today, 'month') ||
      currentDate.value.isSame(today, 'month')
    );
  }
  return (
    currentDate.value.isBefore(today, 'week') ||
    currentDate.value.isSame(today, 'week')
  );
});

const currentPeriodLabel = computed(() => {
  if (calendarView.value === 'month') {
    return currentDate.value.format('MMMM YYYY').toUpperCase();
  }
  const s = currentDate.value.startOf('isoWeek');
  const e = currentDate.value.endOf('isoWeek');
  if (s.month() === e.month())
    return `${s.format('D')} – ${e.format('D MMMM YYYY')}`.toUpperCase();
  return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`.toUpperCase();
});

const dateRange = computed(() => {
  let startDate: typeof dayjs.prototype;
  let endDate: typeof dayjs.prototype;
  if (calendarView.value === 'month') {
    startDate = currentDate.value.startOf('month').startOf('isoWeek');
    endDate = currentDate.value.endOf('month').endOf('isoWeek');
  } else {
    startDate = currentDate.value.startOf('isoWeek');
    endDate = currentDate.value.endOf('isoWeek');
  }
  return {
    start: startDate.format('YYYY-MM-DD'),
    end: endDate.format('YYYY-MM-DD'),
  };
});

const {
  data: eventsByDate,
  pending,
  error: calendarError,
  refresh,
} = useAsyncData<EventsByDate[]>(
  () => `events-by-date-${dateRange.value.start}-${dateRange.value.end}`,
  () =>
    fetchWithAuth<EventsByDate[]>(API_ENDPOINTS.EVENTS_BY_DATE, {
      query: { startDate: dateRange.value.start, endDate: dateRange.value.end },
    }),
  { default: () => [], watch: [dateRange] },
);

watch(calendarError, (error) => {
  if (error) notify(extractApiErrorMessage(error), 'error');
});

const selectedDayEvents = computed(() => {
  if (!selectedDay.value) return [];
  return (
    eventsByDate.value?.find((d) => d.date === selectedDay.value)?.events ?? []
  );
});

const selectedDayObj = computed(() =>
  selectedDay.value ? dayjs(selectedDay.value) : null,
);

const navigatePrevious = () => {
  if (isPastPeriod.value) return;
  currentDate.value =
    calendarView.value === 'month'
      ? currentDate.value.subtract(1, 'month')
      : currentDate.value.subtract(1, 'week');
  selectedDay.value = null;
};
const navigateNext = () => {
  currentDate.value =
    calendarView.value === 'month'
      ? currentDate.value.add(1, 'month')
      : currentDate.value.add(1, 'week');
  selectedDay.value = null;
};
const goToToday = () => {
  currentDate.value = dayjs();
  selectedDay.value = dayjs().format('YYYY-MM-DD');
};

watch([calendarView, currentDate], () => {
  updateQueryParams();
});
</script>
